import React from 'react';

/**
 * Shared chart container — used by every dashboard in the app.
 *
 * Owns the card shell, the heading and the empty state, so no dashboard
 * has to repeat `bg-white border border-gray-100 rounded-xl p-5 shadow-sm`.
 * The chart itself is passed in as children.
 *
 *   <ChartCard title="OT Hours by Month" subtitle="Per calendar month" empty={!rows.length}>
 *     <ResponsiveContainer width="100%" height={240}>...</ResponsiveContainer>
 *   </ChartCard>
 *
 * @param {string}   title
 * @param {string}   [subtitle]   Small grey line under the title
 * @param {Function} [icon]       lucide-react icon component (not an element)
 * @param {boolean}  [empty]      Render the empty state instead of children
 * @param {string}   [emptyText]
 * @param {number}   [emptyHeight] Height of the empty state box, keeps rows aligned
 * @param {Node}     [footer]     Rendered below the chart, e.g. a legend row
 * @param {string}   [className]
 */
export default function ChartCard({
  title,
  subtitle,
  icon: Icon,
  empty = false,
  emptyText = 'No data',
  emptyHeight = 220,
  footer,
  className = '',
  children,
}) {
  return (
    <div className={`bg-white border border-gray-100 rounded-xl p-5 shadow-sm h-full flex flex-col ${className}`}>
      {(title || subtitle) && (
        <div className="mb-4 shrink-0">
          <div className="flex items-center gap-1.5">
            {Icon && <Icon size={14} className="text-primary-600 shrink-0" />}
            <h3 className="text-sm font-semibold text-gray-800 truncate">{title}</h3>
          </div>
          {subtitle && <p className="text-[11px] text-gray-400 mt-0.5">{subtitle}</p>}
        </div>
      )}

      <div className="flex-1 min-h-0">
        {empty ? (
          <div
            className="flex items-center justify-center text-gray-400 text-sm"
            style={{ height: emptyHeight }}
          >
            {emptyText}
          </div>
        ) : (
          children
        )}
      </div>

      {footer && <div className="shrink-0 mt-2">{footer}</div>}
    </div>
  );
}
